import React, { useEffect } from 'react'
import { useHistory, NavLink } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { STORAGE_KEY_VOUCHER_APPLICABLE_PLANS } from '../../../../storage-keys'
import { segmentTrackCompletePurchase } from '../../../../segment/segment-track'
import { dataLayerPurchasePlan } from '../../../../lib/analytics/datalayer'
import { signUp } from '../../../../lib/analytics/events'
import { useIsAuthenticated } from '../../../../hooks/use-is-authenticated'
import { usePlanDetails } from '../../../shared/hooks/use-plan-details'
import { useSubscriptionConfirmation } from '../../hooks/use-subscription-confirmation'
import { links } from '../../../content-onboarding/constants'
import { planType } from '../../../shared/subscription-constants'
import { firstPaymentLabel, firstPaymentDueLabel } from '../../../change-subscription/constants'
import { SIGN_UP_SCREENS } from '../../../../constants'
import FullScreenLoadingComponent from '../../../../components/loading/fullscreen-loader'
import Button from '../../../../components/button'
import PlanDetails from '../../../../components/plan-details'
import styles from '../sign-up.css'

const Confirmation = () => {
  const currentScreen = SIGN_UP_SCREENS.CONFIRMATION
  const history = useHistory()
  const isAuthenticated = useIsAuthenticated()
  const { getPlanDetailByTitle } = usePlanDetails()

  const {
    loading,
    details,
    accountData,
    orderId,
    voucher,
    hasSubscription,
    isTvodPlan,
    plan
  } = useSubscriptionConfirmation()

  const accountId = useSelector(state => state.session.decodedJwt?.accountId || '')

  const isValidPlan = plan && Object.values(planType).includes(plan)

  const isConfirmationVisible = isTvodPlan || hasSubscription

  useEffect(() => {
    if (!isAuthenticated || !isConfirmationVisible || !isValidPlan) {
      history.push('/')
    }
  }, [isAuthenticated, isConfirmationVisible, isValidPlan, history])

  useEffect(() => {
    if (!isConfirmationVisible) return

    signUp.stepViewed(currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel, plan)
    signUp.complete(accountId, hasSubscription ? orderId : null, hasSubscription, plan)

    // Clear the voucher info in session storage
    sessionStorage.removeItem(STORAGE_KEY_VOUCHER_APPLICABLE_PLANS)
  }, [])

  // Add segment data analytics for completing purchase
  useEffect(() => {
    if (loading) return
    if (!isConfirmationVisible) return
    if (!details.length || !accountData) return

    const paymentDetail = details.find(e => e.name === firstPaymentLabel)
    const paymentDueDetail = details.find(e => e.name === firstPaymentDueLabel)
    if (!paymentDetail || !paymentDueDetail) return

    segmentTrackCompletePurchase({
      planTitle: plan,
      planSku: getPlanDetailByTitle(plan)?.sku,
      paymentDue: paymentDueDetail.value,
      payment: Number(paymentDetail.value?.replace('$', '')),
      orderId,
      voucher
    })
  }, [loading])

  // data layer 'purchase' event after completing subscription
  useEffect(() => {
    if (accountData?.account && orderId) {
      dataLayerPurchasePlan(accountData.account, orderId)
    }
  }, [accountData])

  const header = `You’re good to go!`

  return (
    <>
      <div className={styles.topContainer}>
        <div className={styles.header}>{header}</div>
        <div className={styles.subHeader}>
          Next, set up your unique profile.
        </div>
      </div>

      <div className={styles.container}>
        {!loading && isConfirmationVisible && (
          <>
            {!isTvodPlan && <PlanDetails detail={details} showVoucherMessage={voucher} />}

            <div className={styles.content}>
              <NavLink to={links.profileSelection}>
                <Button
                  text="Set up your profile"
                />
              </NavLink>
              <NavLink to="/">
                <Button
                  text="Skip & Start Watching"
                  darkBGSecondary
                />
              </NavLink>
            </div>
          </>
        )}
        <FullScreenLoadingComponent pastDelay={loading} data-e2e="signup-loader-visible" />
      </div>
    </>
  )
}

export default Confirmation
